
import { useEffect, useState, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Json } from "@/integrations/supabase/types";

export interface Notification {
  id: string;
  recipient_id: string;
  actor_id: string | null;
  type: string;
  content_id: string;
  content_type: string;
  is_read: boolean;
  created_at: string;
  metadata: Json;
  sender?: {
    username?: string;
    avatar_url?: string;
  } | null;
  message?: string;
}

export const useNotifications = (userId?: string) => {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchNotifications = useCallback(async () => {
    if (!userId) return;
    
    try {
      setLoading(true);
      
      const { data, error } = await supabase
        .rpc('get_notifications_with_profiles', { user_id: userId });
        
      if (error) {
        throw error;
      }
      
      const formattedNotifications = data.map((notification: any) => {
        let message = '';
        
        if (notification.actor_profile) {
          const actorName = notification.actor_profile.username || 'Alguém';
          
          switch (notification.type) {
            case 'welcome':
              message = `Bem-vindo(a) ao Ink Webtoons!`;
              break;
            case 'like':
              message = `${actorName} curtiu seu conteúdo`;
              break;
            case 'comment':
              message = `${actorName} comentou em seu conteúdo`;
              break;
            case 'follow':
              message = `${actorName} começou a seguir você`;
              break;
            case 'save':
              message = `${actorName} salvou seu conteúdo`;
              break;
            case 'share':
              message = `${actorName} compartilhou seu conteúdo`;
              break;
            default:
              message = `${actorName} interagiu com seu conteúdo`;
          }
        } else if (notification.type === 'welcome') {
          message = `Bem-vindo(a) ao Ink Webtoons!`;
        }
        
        return {
          ...notification,
          sender: notification.actor_profile,
          message
        };
      });
      
      setNotifications(formattedNotifications);
      setUnreadCount(formattedNotifications.filter((n: Notification) => !n.is_read).length);
      
    } catch (err: any) {
      console.error("Error fetching notifications:", err);
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  const markAsRead = async (notificationId: string) => {
    try {
      const { error } = await supabase
        .from("notifications")
        .update({ is_read: true })
        .eq("id", notificationId);
        
      if (error) {
        throw error;
      }
      
      setNotifications(prev => 
        prev.map(notification => 
          notification.id === notificationId 
            ? { ...notification, is_read: true } 
            : notification
        )
      );
      
      setUnreadCount(prev => prev - 1);
      
    } catch (err) {
      console.error("Error marking notification as read:", err);
      toast.error("Erro ao marcar notificação como lida");
    }
  };

  const markAllAsRead = async () => {
    if (!userId) return;
    
    try {
      const { error } = await supabase
        .from("notifications")
        .update({ is_read: true })
        .eq("recipient_id", userId)
        .eq("is_read", false);
        
      if (error) {
        throw error;
      }
      
      setNotifications(prev => 
        prev.map(notification => ({ ...notification, is_read: true }))
      );
      
      setUnreadCount(0);
      
      toast.success("Todas as notificações foram marcadas como lidas");
      
    } catch (err) {
      console.error("Error marking all notifications as read:", err);
      toast.error("Erro ao marcar todas notificações como lidas");
    }
  };

  const deleteNotification = async (notificationId: string) => {
    try {
      const { error } = await supabase
        .from("notifications")
        .delete()
        .eq("id", notificationId);
        
      if (error) {
        throw error;
      }
      
      setNotifications(prev => 
        prev.filter(notification => notification.id !== notificationId)
      );
      
      const deletedNotification = notifications.find(n => n.id === notificationId);
      if (deletedNotification && !deletedNotification.is_read) {
        setUnreadCount(prev => prev - 1);
      }
      
    } catch (err) {
      console.error("Error deleting notification:", err);
      toast.error("Erro ao excluir notificação");
    }
  };

  const clearAllNotifications = async () => {
    if (!userId) return;
    
    try {
      const { error } = await supabase
        .from("notifications")
        .delete()
        .eq("recipient_id", userId);
        
      if (error) {
        throw error;
      }
      
      setNotifications([]);
      setUnreadCount(0);
      
      toast.success("Todas as notificações foram removidas");
      
    } catch (err) {
      console.error("Error clearing all notifications:", err);
      toast.error("Erro ao limpar todas as notificações");
    }
  };

  const createWelcomeNotification = useCallback(async () => {
    if (!userId) return;
    
    try {
      // First check if welcome notification already exists
      const { data, error } = await supabase
        .from("notifications")
        .select("id")
        .eq("recipient_id", userId)
        .eq("type", "welcome")
        .maybeSingle();
        
      if (error) {
        throw error;
      }
      
      // If welcome notification already exists, don't create another one
      if (data) {
        console.log("Welcome notification already exists");
        return;
      }
      
      // Create a welcome notification manually with the new image
      const { error: insertError } = await supabase
        .from("notifications")
        .insert({
          recipient_id: userId,
          actor_id: null,
          type: 'welcome',
          content_id: userId,
          content_type: 'system',
          is_read: false,
          metadata: {
            welcome_image: "/lovable-uploads/c950070c-60a0-4c18-a0f2-efe41a37b71a.png"
          }
        });
      
      if (insertError) {
        console.error("Error creating welcome notification:", insertError);
        throw insertError;
      }
      
      console.log("Welcome notification created successfully");
      fetchNotifications();
      
    } catch (err) {
      console.error("Error creating welcome notification:", err);
    }
  }, [userId, fetchNotifications]);

  useEffect(() => {
    if (userId) {
      fetchNotifications();
      
      const subscription = supabase
        .channel('notifications-changes')
        .on('postgres_changes', { 
          event: '*', 
          schema: 'public',
          table: 'notifications',
          filter: `recipient_id=eq.${userId}`
        }, () => {
          fetchNotifications();
        })
        .subscribe();
      
      return () => {
        supabase.removeChannel(subscription);
      };
    }
  }, [userId, fetchNotifications]);

  return {
    notifications,
    unreadCount,
    loading,
    error,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    clearAllNotifications,
    createWelcomeNotification,
    refresh: fetchNotifications
  };
};
